import {
  Flex,
  HStack,
  Menu,
  Separator,
  Text,
  useBreakpointValue
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { LearningClassEnums as CLS } from "@/ts/enums/LearningClassEnums";
import {
  AvatarImage,
  ColorModeButton,
  GradeTheme,
  Logo,
  NavigationIcons,
  STUDENT_AVATAR_OPTIONS,
  getStudentLogo,
  getTrackingId
} from "@euka-mfes/bark";
import ImpersonationBanner from "@components/ImpersonationBanner";

import { useStudentData } from "@/context";
import primaryDarkBgImage from "@assets/header-primary-dark.png";
import primaryLightBgImage from "@assets/header-primary-light.png";
import secondaryBgImage from "@assets/header-secondary.jpg";
import seniorBgImage from "@assets/header-senior.jpg";
import LessonHeaderBreadcrumb from "@components/lessons/LessonHeaderBreadcrumb";
import GradeActivationModals from "@components/student/GradeActivationModals";
import useCrossTabLessonSync from "@hooks/useCrossTabLessonSync";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { getCurrentSectionFromPath } from "@utils/lessonUtils";
import { logger, setDatadogUserContext } from "@utils/loggingUtils";
import { getAccountRoute, getHomeRoute } from "@utils/route";
import {
  getOrientationStartUrl,
  shouldRedirectToOrientation
} from "@/utils/orientation";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type MouseEvent
} from "react";

const HEADER_HEIGHT_VAR = "--euka-header-height";

interface HeaderProps {
  showBreadcrumb?: boolean;
  isOrientation?: boolean;
}

const getHeaderStyles = (): Record<
  GradeTheme,
  {
    color: string;
    backgroundImageLight: string;
    backgroundImageDark: string;
    backgroundPosition: string;
    backgroundRepeat: string;
    backgroundSize: string;
    backgroundOrigin: string;
    borderBottom: string;
    borderBottomStyle: string;
    borderBottomColor: string;
    textColor: string;
    headerHeight: { base: string; md: string } | string;
    iconColor: string;
    separatorColor: string;
    themeSwitchColor: string;
  }
> => ({
  PRIMARY: {
    color: "gradeTheme.headerBg",
    backgroundImageLight: `url(${primaryLightBgImage})`,
    backgroundImageDark: `url(${primaryDarkBgImage})`,
    backgroundPosition: "bottom left",
    backgroundRepeat: "no-repeat",
    backgroundSize: "100% auto",
    backgroundOrigin: "padding-box",
    borderBottom: "0px",
    borderBottomStyle: "solid",
    borderBottomColor: "gradeTheme.headerDivider",
    textColor: "gradeTheme.headerText",
    headerHeight: "96px",
    iconColor: "gradeTheme.iconColor",
    separatorColor: "gradeTheme.iconColor",
    themeSwitchColor: "gradeTheme.themeSwitchColor"
  },
  SECONDARY: {
    color: "gradeTheme.headerBg",
    backgroundImageLight: `url(${secondaryBgImage})`,
    backgroundImageDark: `url(${secondaryBgImage})`,
    backgroundPosition: "top left",
    backgroundRepeat: "repeat-x",
    backgroundSize: "none",
    backgroundOrigin: "padding-box",
    borderBottom: "0px",
    borderBottomStyle: "solid",
    borderBottomColor: "gradeTheme.headerDivider",
    textColor: "gradeTheme.headerText",
    headerHeight: "96px",
    iconColor: "gradeTheme.iconColor",
    separatorColor: "gradeTheme.iconColor",
    themeSwitchColor: "gradeTheme.themeSwitchColor"
  },
  SENIOR: {
    color: "gradeTheme.headerBg",
    backgroundImageLight: `url(${seniorBgImage})`,
    backgroundImageDark: `url(${seniorBgImage})`,
    backgroundPosition: "top left",
    backgroundRepeat: "repeat-x",
    backgroundSize: "none",
    backgroundOrigin: "padding-box",
    borderBottom: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: "gradeTheme.headerDivider",
    textColor: "gradeTheme.headerText",
    headerHeight: "96px",
    iconColor: "gradeTheme.iconColor",
    separatorColor: "gradeTheme.iconColor",
    themeSwitchColor: "gradeTheme.themeSwitchColor"
  }
});

const Header = ({
  showBreadcrumb = false,
  isOrientation = false
}: HeaderProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const headerStyles = getHeaderStyles();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Get everything from context
  const {
    headerDetails,
    headerDetailsStatus,
    selectedGrade,
    gradeTheme,
    hasAttemptedApplyQuestion,
    setHasAttemptedApplyQuestion,
    isApplyResultViewShown,
    setShowBlockNavigationModal,
    setPendingNavigationPath,
    openGradePicker
  } = useStudentData();

  const currentSection = getCurrentSectionFromPath(pathname);

  // Cross-tab sync
  useCrossTabLessonSync();

  const {
    color,
    backgroundImageLight,
    backgroundImageDark,
    backgroundRepeat,
    backgroundSize,
    backgroundPosition,
    borderBottom,
    borderBottomStyle,
    borderBottomColor,
    textColor,
    headerHeight,
    iconColor,
    separatorColor
  } = headerStyles[gradeTheme];

  const headerContainerRef = useRef<HTMLDivElement | null>(null);

  const handleAccountClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (currentSection === "apply") {
      event.preventDefault();

      if (hasAttemptedApplyQuestion && !isApplyResultViewShown) {
        setPendingNavigationPath(
          getAccountRoute(selectedGrade?.grade_code as number)
        );
        setShowBlockNavigationModal(true);
      } else {
        navigate(getAccountRoute(selectedGrade?.grade_code as number));
        setHasAttemptedApplyQuestion(false);
      }
    }
  };


  useEffect(() => {
    if (!selectedGrade ||
        pathname.includes("/orientation") ||
        !shouldRedirectToOrientation({
            orientationEnabled: headerDetails.orientationEnabled,
            grade: selectedGrade,
        })
    )
      return;

    logger.log(
      "Redirecting to orientation for grade",
      selectedGrade.grade_code
    );

    navigate(
      getOrientationStartUrl(
        selectedGrade.grade_code,
        headerDetails.userState?.introScreenSeen
      ),
      {
        replace: true
      }
    );
  }, [
    selectedGrade?.grade_code,
    selectedGrade?.orientation_completed,
    headerDetails.orientationEnabled,
    headerDetails.userState?.introScreenSeen,
    pathname,
    navigate
  ]);

  useEffect(() => {
    const studentUser = headerDetails.student;
    if (studentUser) {
      setDatadogUserContext({
        id: studentUser.username,
        eukaId: studentUser.username,
        role: "student"
      });
    }
  }, [headerDetails.student]);

  const syncHeaderHeight = useCallback(() => {
    const node = headerContainerRef.current;
    if (!node) return;

    document.documentElement.style.setProperty(
      HEADER_HEIGHT_VAR,
      `${node.offsetHeight}px`
    );
  }, []);

  // Publish the full header height (including impersonation banner and
  // decorative wave) as a CSS variable so layout consumers can reference
  // it without hardcoded values.
  useLayoutEffect(() => {
    syncHeaderHeight();

    const node = headerContainerRef.current;
    if (!node) return;

    const rafId = window.requestAnimationFrame(syncHeaderHeight);
    const timeoutId = window.setTimeout(syncHeaderHeight, 0);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(syncHeaderHeight)
        : null;
    resizeObserver?.observe(node);

    const mutationObserver = new MutationObserver(syncHeaderHeight);
    mutationObserver.observe(node, {
      childList: true,
      subtree: true,
      characterData: true
    });

    window.addEventListener("resize", syncHeaderHeight);
    window.visualViewport?.addEventListener("resize", syncHeaderHeight);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", syncHeaderHeight);
      window.visualViewport?.removeEventListener("resize", syncHeaderHeight);
    };
  }, [syncHeaderHeight]);

  return (
    <Flex
      ref={headerContainerRef}
      className="app-header"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex="modal"
      w="100%"
      minW="100%"
      direction="column"
      data-sidebar-persistent="true"
    >
      <ImpersonationBanner />
      <Flex
        data-testid="header-row"
        direction="row"
        alignItems="center"
        justify="space-between"
        alignContent="center"
        minW="100%"
        w="100%"
        paddingLeft={{ base: 4, md: 10 }}
        paddingRight={{ base: 4, md: 10 }}
        height={headerHeight}
        css={{
          WebkitTransform: "translateZ(0)",
          transform: "translateZ(0)",
          backgroundRepeat,
          backgroundColor: color,
          backgroundPosition,
          backgroundSize,
          borderBottom,
          borderBottomStyle,
          borderBottomColor,
          backgroundOrigin: "border-box",
          _light: {
            backgroundImage:
              gradeTheme === GradeTheme.Primary ? "none" : backgroundImageLight
          },
          _dark: {
            backgroundImage:
              gradeTheme === GradeTheme.Primary ? "none" : backgroundImageDark
          }
        }}
      >
        <Flex gap={4} alignItems="center">
          <HStack gap="2">
            {showBreadcrumb ? (
              <LessonHeaderBreadcrumb textColor={textColor} />
            ) : (
              <>
                <Link
                  to={getHomeRoute(selectedGrade?.grade_code as number)}
                  aria-disabled={isOrientation}
                  tabIndex={isOrientation ? -1 : undefined}
                  onClick={
                    isOrientation
                      ? (e: MouseEvent<HTMLAnchorElement>) => e.preventDefault()
                      : undefined
                  }
                  style={isOrientation ? { pointerEvents: "none" } : undefined}
                  data-tracking-id={getTrackingId(
                    [CLS.Header, CLS.Link],
                    "logo-home"
                  )}
                >
                  <Logo
                    variant={isMobile ? "brandmark" : "inline"}
                    color="gradeTheme.headerLogo"
                    size={isMobile ? "md" : "xl"}
                    isDarkMint={false}
                  />
                </Link>

                {!isOrientation && (
                  <>
                    <Separator
                      className="print-hide"
                      orientation="vertical"
                      height={8}
                      size="sm"
                      borderColor={separatorColor}
                      ml="3"
                    />

                    {headerDetailsStatus !== REQUEST_STATUS.PENDING &&
                      headerDetails.grades.length > 0 && (
                        <Flex
                          className="print-hide"
                          alignItems="center"
                          padding="4px 8px"
                          ml="8px"
                          {...(headerDetails.grades.length > 1 && {
                            _hover: {
                              "backgroundColor": "button.bg.filledHover",
                              "cursor": "pointer",
                              "borderRadius": "4px",
                              "& *": {
                                color: "base.200"
                              }
                            }
                          })}
                          onClick={
                            headerDetails.grades.length > 1
                              ? () => openGradePicker()
                              : undefined
                          }
                          data-tracking-id={getTrackingId(
                            [CLS.Header, CLS.Button],
                            "grade-selector"
                          )}
                        >
                          <Text
                            textStyle="title.3"
                            color={textColor}
                            data-testid="selected-grade-label"
                          >
                            {selectedGrade?.name}
                          </Text>
                          {headerDetails.grades.length > 1 && (
                            <Menu.Root
                              positioning={{ placement: "right-start" }}
                              closeOnSelect={false}
                            >
                              <Menu.Trigger asChild>
                                <NavigationIcons.Dots
                                  size="lg"
                                  color={iconColor}
                                  cursor="pointer"
                                />
                              </Menu.Trigger>
                            </Menu.Root>
                          )}
                        </Flex>
                      )}
                  </>
                )}
              </>
            )}
          </HStack>
        </Flex>

        <Flex gap={3} alignItems="center" className="print-hide">
          <Link
            to={getAccountRoute(selectedGrade?.grade_code as number)}
            onClick={
              isOrientation
                ? (e: MouseEvent<HTMLAnchorElement>) => e.preventDefault()
                : handleAccountClick
            }
            aria-disabled={isOrientation}
            tabIndex={isOrientation ? -1 : undefined}
            style={isOrientation ? { pointerEvents: "none" } : undefined}
            data-testid="header-account-link"
            data-tracking-id={getTrackingId(
              [CLS.Header, CLS.Link],
              "account-link"
            )}
          >
            <Flex gap={4} alignItems="center">
              <Text
                textStyle="body.large"
                fontWeight="semibold"
                display={{ base: "none", md: "block" }}
                color={textColor}
              >
                {headerDetails.student?.first_name}
              </Text>
              <AvatarImage
                size="xl"
                displayName={headerDetails.student?.first_name}
                profileImageSrc={getStudentLogo(
                  headerDetails.student?.avatar_key ?? 0
                )}
                trackingId={getTrackingId(
                  [CLS.Header, CLS.Avatar],
                  `student-avatar-${
                    STUDENT_AVATAR_OPTIONS.find(
                      a => a.id === Number(headerDetails.student?.avatar_key ?? 0)
                    )?.name ?? "unknown"
                  }`
                )}
              />
            </Flex>
          </Link>

          {/* theme switch */}
          <ColorModeButton
            darkModeIcon={
              <NavigationIcons.Moonlight
                color="gradeTheme.colorModeIcon"
                boxSize="12"
              />
            }
            lightModeIcon={
              <NavigationIcons.Sunlight
                color="gradeTheme.colorModeIcon"
                boxSize="12"
              />
            }
            data-tracking-id={getTrackingId(
              [CLS.Header, CLS.Button],
              "color-mode-toggle"
            )}
          />
        </Flex>
      </Flex>
      {gradeTheme === GradeTheme.Primary && (
        <Flex
          className="header-decorative-wave"
          w="100%"
          h={{ base: "12px", md: "16px" }}
          justify="center"
          alignItems="center"
          bgColor="transparent"
          css={{
            WebkitTransform: "translateZ(0)",
            transform: "translateZ(0)",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "bottom",
            backgroundSize: "100% 100%",
            backgroundOrigin: "border-box",
            _light: {
              backgroundImage: backgroundImageLight
            },
            _dark: {
              backgroundImage: backgroundImageDark
            }
          }}
        />
      )}

      <GradeActivationModals />
    </Flex>
  );
};

export default Header;
